import { Box, ScaleFactorProvider, useLayoutContext } from "arkh";
import React, { useCallback, useMemo, useRef } from "react";
import { graphql, useFragment, useMutation } from "react-relay";
import { StorecastSpace_storecastSpace$key } from "./__generated__/StorecastSpace_storecastSpace.graphql";
import { InteractiveWidgetWrapper } from "../../Storecast/Storecast/Widget/InteractiveWidgetWrapper";
import Animated, {
  SharedValue,
  useAnimatedRef,
  useSharedValue,
} from "react-native-reanimated";

import { StorecastSpaceEmptyPositions } from "./StorecastSpaceEmptyPositions";
import { StorecastSpaceSetPositionsMutation } from "./__generated__/StorecastSpaceSetPositionsMutation.graphql";
import { WidgetNote } from "widgets";
import { WidgetEvent } from "widgets/src/WidgetEventT";
import { WidgetSizeT } from "../../Storecast/Storecast/localTypes/WidgetSizeT";
import { StorecastSpaceDeleteWidgetMutation } from "./__generated__/StorecastSpaceDeleteWidgetMutation.graphql";
import { View } from "react-native";
import { useWidgetMargin } from "../../Storecast/Storecast/Widget/useWidgetMargin";
import { WidgetFile } from "widgets/src/WidgetFile/WidgetFile";
import { captureException } from "../../../common/sentry";
import { ErrorBoundary } from "react-error-boundary";
import { StorecastSpaceWidget_subWidget$key } from "./__generated__/StorecastSpaceWidget_subWidget.graphql";
import { WidgetErrorFallback } from "../../Storecast/Storecast/WidgetErrorFallback";
import { useOrgSpaceUnitSize } from "./useOrgSpaceUnitSize";
import { useWorkspacePermissions } from "../workspaceId";
import { WidgetPos } from "widgets/src/utils/useWidgetToPos";

const STORECAST_SPACE_BORDER_WIDTH = 3;

export function useGetStorecastSpaceHeight() {
  const unitSize = useOrgSpaceUnitSize();
  const layout = useLayoutContext();
  return useCallback(
    (height: number) => {
      return (
        height * unitSize +
        (layout.space.medium + STORECAST_SPACE_BORDER_WIDTH) * 2
      );
    },
    [layout.space.medium, unitSize]
  );
}

export function StorecastSpace(props: {
  unitSize: number;
  space: StorecastSpace_storecastSpace$key;
  isEditing: boolean;
  openWidgetSettings?(_: {
    widgetId: string;
    __typename: string;
    size: WidgetSizeT;
  }): void;
  onAddWidget: (_: { y: number; h: number }) => void;
  onEvent?:
    | ((_: { event: WidgetEvent; x: number; y: number }) => any)
    | undefined;
  hiddenWidgetId: SharedValue<string | null>;
}) {
  const layout = useLayoutContext();
  const space = useFragment(
    graphql`
      fragment StorecastSpace_storecastSpace on StorecastSpace {
        id
        height
        subWidgets {
          id
          height
          positionX
          positionY
          __typename
          ...InteractiveWidgetWrapper_widget
          ...StorecastSpaceWidget_subWidget
        }
      }
    `,
    props.space
  );

  const widgetPositions = useSharedValue<WidgetPos[]>(
    space.subWidgets.map((w) => ({
      y: w.positionY,
      h: w.height,
      id: w.id,
      kind: w.__typename,
      w: 2,
      x: 0,
      c: 0,
    }))
  );

  useMemo(() => {
    widgetPositions.value = space.subWidgets.map((w) => ({
      y: w.positionY,
      h: w.height,
      id: w.id,
      kind: w.__typename,
      w: 2,
      x: 0,
      c: 0,
    }));
  }, [space.subWidgets, widgetPositions]);

  const scrollRef = useAnimatedRef<Animated.ScrollView>();
  const scrollX = useSharedValue(0);

  const [deleteWidget] = useMutation<StorecastSpaceDeleteWidgetMutation>(
    graphql`
      mutation StorecastSpaceDeleteWidgetMutation($id: ID!, $widgetId: ID!) {
        storecastSpace(id: $id) {
          deleteWidget(id: $widgetId) {
            id
            subWidgets {
              id
            }
          }
        }
      }
    `
  );

  const onWidgetDelete = React.useCallback(
    (widgetId: string) => {
      deleteWidget({
        variables: {
          id: space.id,
          widgetId,
        },
      });
    },
    [deleteWidget, space.id]
  );

  const [_setPositions] = useMutation<StorecastSpaceSetPositionsMutation>(
    graphql`
      mutation StorecastSpaceSetPositionsMutation(
        $id: ID!
        $positions: [StorecastSpacePositionInput!]!
      ) {
        storecastSpace(id: $id) {
          setWidgetPositions(positions: $positions) {
            id
            subWidgets {
              id
              height
              positionX
              positionY
              ... on WidgetNote {
                noteSize
                note {
                  noteSize
                }
              }
            }
          }
        }
      }
    `
  );

  const setPositions = React.useCallback(
    (pos: WidgetPos[]) => {
      _setPositions({
        variables: {
          id: space.id,
          positions: pos.map((p) => ({
            id: p.id,
            x: p.x,
            y: p.y,
            width: 2,
            height: p.h,
          })),
        },
        onCompleted: (data) => {
          console.log("onCompleted", data);
        },
        onError: (error) => {
          console.log("onError", error);
        },
      });
    },
    [space.id, _setPositions]
  );

  const layoutRef = useRef<View>(null);
  const widgetMargin = useWidgetMargin();

  const onEvent = useCallback(
    (event: WidgetEvent, subWidgetId: string) => {
      layoutRef.current?.measure((x, y, width, height, pageX, pageY) => {
        props.onEvent?.({
          x:
            pageX +
            layout.space.medium +
            STORECAST_SPACE_BORDER_WIDTH +
            widgetMargin,
          y:
            pageY +
            layout.space.medium +
            STORECAST_SPACE_BORDER_WIDTH +
            widgetMargin +
            (widgetPositions.value?.find((w) => w.id === subWidgetId)?.y ?? 0) *
              props.unitSize,
          event,
        });
      });
    },
    [layout.space.medium, props, widgetMargin, widgetPositions.value]
  );
  const getStorecastSpaceHeight = useGetStorecastSpaceHeight();

  const permissions = useWorkspacePermissions();
  return (
    <Box
      ref={layoutRef}
      color="surface-background-light"
      cornerRadius="large"
      style={{
        height: getStorecastSpaceHeight(space.height),
        width: props.unitSize * 2 + layout.space.medium * 2 + 3 * 2,
        borderStyle: "dashed",
        borderWidth: STORECAST_SPACE_BORDER_WIDTH,
      }}
      spaceVertical="medium"
      borderColor={props.isEditing ? "primary" : "border-primary"}
    >
      <ScaleFactorProvider value={props.unitSize / 236}>
        <Box style={{ flex: 1 }}>
          {permissions.write ? (
            <StorecastSpaceEmptyPositions
              isEditing={props.isEditing}
              positions={widgetPositions}
              unitSize={props.unitSize}
              numberOfRows={space.height}
              onAddWidget={props.onAddWidget}
            />
          ) : null}
          {space.subWidgets.map((subWidget) => (
            <InteractiveWidgetWrapper
              key={subWidget.id}
              canEdit={permissions.write}
              numberOfColumns={1}
              renderContext="space"
              numberOfRows={space.height}
              unitSize={props.unitSize}
              widget={subWidget}
              hiddenWidgetId={props.hiddenWidgetId}
              isEditing={props.isEditing}
              onRequestEdit={() => {}}
              requestDeleteWidget={onWidgetDelete}
              scrollRef={scrollRef}
              openSettings={props.openWidgetSettings}
              scrollX={scrollX}
              setWidgets={setPositions}
              spaceBetweenColumns={layout.space.medium * 2}
              widgets={widgetPositions}
            >
              <Widget onEvent={onEvent} widget={subWidget} />
            </InteractiveWidgetWrapper>
          ))}
        </Box>
      </ScaleFactorProvider>
    </Box>
  );
}

function Widget(props: {
  onEvent: (event: WidgetEvent, widgetId: string) => void;
  widget: StorecastSpaceWidget_subWidget$key;
}) {
  const subWidget = useFragment(
    graphql`
      fragment StorecastSpaceWidget_subWidget on Widget {
        id
        __typename
        ... on WidgetNote {
          ...WidgetNote_widgetNote
        }
        ... on WidgetFile {
          ...WidgetFile_widgetFile
        }
      }
    `,
    props.widget
  );
  const [errorKey, setErrorKey] = React.useState(0);
  const onReset = useCallback(() => setErrorKey((k) => k + 1), []);
  const onError = useCallback(
    (e: Error, info: { componentStack: string }): void => {
      captureException(e, { extra: { info } });
    },
    []
  );
  const permissions = useWorkspacePermissions();

  return (
    <ErrorBoundary
      key={errorKey}
      onError={onError}
      onReset={onReset}
      FallbackComponent={WidgetErrorFallback}
    >
      {subWidget.__typename === "WidgetNote" ? (
        <WidgetNote
          widget={subWidget}
          mode="IDLE"
          idiom="TOUCH"
          onEvent={(event) => props.onEvent(event, subWidget.id)}
          isRenderedFromOrg={permissions.write}
        />
      ) : subWidget.__typename === "WidgetFile" ? (
        <WidgetFile
          widget={subWidget}
          mode="IDLE"
          idiom="TOUCH"
          onEvent={(event) => {
            props.onEvent(event, subWidget.id);
          }}
        />
      ) : null}
    </ErrorBoundary>
  );
}
